import React, { useState } from 'react';
import GitKontoBereich from '../components/GitKontoBereich';
import SshSchluesselPanel from '../components/SshSchluesselPanel';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Lock, Unlock, KeyRound, ScrollText, X, ShieldCheck, ShieldOff} from 'lucide-react';
import { users as usersApi, registryCredentials } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useDateFormat } from '../lib/dates';
import DataTable, { Column } from '../components/DataTable';
import { ROLE_LABELS, ROLE_DESCRIPTIONS } from '../types';
import type { AuditLog, User, UserRole, UserStatus } from '../types';

const ALL_ROLES: UserRole[] = [
  'ADMIN',
  'OPERATIONS',
  'PRODUCT_MANAGER',
  'SUPPORT',
  'SALES',
  'BILLING',
  'MARKETING',
];

const STATUS_STYLES: Record<UserStatus, string> = {
  ACTIVE: 'bg-green-50 text-green-700 border-green-200',
  INACTIVE: 'bg-slate-100 text-slate-600 border-slate-200',
  SUSPENDED: 'bg-red-50 text-red-700 border-red-200',
};

const STATUS_LABELS: Record<UserStatus, string> = {
  ACTIVE: 'Aktiv',
  INACTIVE: 'Inaktiv',
  SUSPENDED: 'Gesperrt',
};

export default function UsersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'ADMIN';

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [activityFor, setActivityFor] = useState<User | null>(null);
  const [error, setError] = useState('');
  const [slackMeldung, setSlackMeldung] = useState('');
  const [notice, setNotice] = useState('');

  const { data: userList, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
  });

  function onError(fallback: string) {
    return (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;
      setError(msg);
      setNotice('');
    };
  }

  function onDone(message: string) {
    return () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setError('');
      setNotice(message);
    };
  }

  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => usersApi.suspend(id, reason),
    onSuccess: onDone('Konto gesperrt.'),
    onError: onError('Sperren fehlgeschlagen.'),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => usersApi.reactivate(id),
    onSuccess: onDone('Konto entsperrt.'),
    onError: onError('Entsperren fehlgeschlagen.'),
  });

  const resetMutation = useMutation({
    mutationFn: (id: string) => usersApi.sendPasswordReset(id),
    onSuccess: onDone('Link zum Zurücksetzen wurde versendet.'),
    onError: onError('Versand fehlgeschlagen.'),
  });

  const reset2faMutation = useMutation({
    mutationFn: (id: string) => usersApi.reset2fa(id),
    onSuccess: onDone('Zwei-Faktor zurückgesetzt — die Person muss es neu einrichten.'),
    onError: onError('Zurücksetzen fehlgeschlagen.'),
  });

  const columns: Column<User>[] = [
    {
      key: 'name',
      label: 'Name',
      render: (u) => (
        <div>
          <div className="font-medium text-slate-900">
            {u.name}
            {u.id === currentUser?.id && (
              <span className="ml-2 text-xs text-slate-400">{t('users.you')}</span>
            )}
          </div>
          <div className="text-xs text-slate-500">{u.email}</div>
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Rolle',
      render: (u) => (
        <span title={ROLE_DESCRIPTIONS[u.role]} className="text-sm text-slate-700">
          {ROLE_LABELS[u.role]}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (u) => (
        <div>
          <span
            className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_STYLES[u.status]}`}
          >
            {STATUS_LABELS[u.status]}
          </span>
          {u.status === 'SUSPENDED' && u.suspendedReason && (
            <div className="text-xs text-slate-400 mt-0.5">{u.suspendedReason}</div>
          )}
          {u.mfaEnabled && (
            <div className="inline-flex items-center gap-1 text-xs text-green-700 mt-1">
              <ShieldCheck size={11} /> 2FA
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'lastLoginAt',
      label: 'Letzter Login',
      render: (u) => (
        <span className="text-xs text-slate-500">
          {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('de-DE') : 'nie'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Aktionen',
      render: (u) => {
        if (!isAdmin) return <span className="text-xs text-slate-300">—</span>;
        const isSelf = u.id === currentUser?.id;
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { setEditing(u); setError(''); }}
              className="text-xs text-brand-600 hover:text-brand-800 font-medium"
            >
              Bearbeiten
            </button>
            <button
              onClick={() => setActivityFor(u)}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 font-medium"
            >
              <ScrollText size={12} /> Aktivität
            </button>
            {u.status === 'ACTIVE' && (
              <button
                onClick={() => resetMutation.mutate(u.id)}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 font-medium"
              >
                <KeyRound size={12} /> Passwort-Link
              </button>
            )}
            {u.mfaEnabled && (
              <button
                onClick={() => {
                  if (
                    confirm(
                      `Zwei-Faktor für ${u.name} zurücksetzen? Nur nötig, wenn Authenticator UND Wiederherstellungscodes verloren sind.`,
                    )
                  ) {
                    reset2faMutation.mutate(u.id);
                  }
                }}
                className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium"
              >
                <ShieldOff size={12} /> 2FA zurücksetzen
              </button>
            )}
            {u.status === 'SUSPENDED' ? (
              <button
                onClick={() => reactivateMutation.mutate(u.id)}
                className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium"
              >
                <Unlock size={12} /> Entsperren
              </button>
            ) : (
              <button
                disabled={isSelf}
                title={isSelf ? 'Sie können Ihr eigenes Konto nicht sperren.' : undefined}
                onClick={() => {
                  const reason = prompt(`Grund für die Sperrung von ${u.name}? (optional)`) ?? undefined;
                  if (reason !== undefined) suspendMutation.mutate({ id: u.id, reason });
                }}
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Lock size={12} /> Sperren
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500">
            {userList?.length ?? 0} Mitarbeitende
            {!isAdmin && ' — nur Administratoren können Konten anlegen oder ändern.'}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                setSlackMeldung('Wird nachgeschlagen …');
                try {
                  const e = await usersApi.slackIdsHolen();
                  setSlackMeldung(
                    e.geprueft === 0
                      ? 'Alle Benutzer haben bereits eine Slack-ID.'
                      : `${e.gefunden} von ${e.geprueft} gefunden.` +
                        (e.ohne.length
                          ? ` Ohne Treffer: ${e.ohne.map((o) => o.name).join(', ')} — ` +
                            'diese Adressen kennt Slack nicht.'
                          : ''),
                  );
                  qc.invalidateQueries({ queryKey: ['users'] });
                } catch (err: any) {
                  setSlackMeldung(err?.response?.data?.message ?? 'Nachschlagen fehlgeschlagen.');
                }
              }}
              className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-400 text-slate-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors"
              title="Holt die Slack-Mitglieds-IDs anhand der Mailadressen. Braucht einen Bot-Token in den Einstellungen."
            >
              Slack-IDs holen
            </button>
            <button
              onClick={() => { setShowCreate(true); setError(''); }}
              className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg transition-colors"
            >
              <Plus size={15} /> Neuer Nutzer
            </button>
          </div>
        )}
      </div>

      {slackMeldung && (
        <div className="bg-slate-50 border border-slate-200 text-slate-700 px-3 py-2 rounded text-sm">
          {slackMeldung}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}
      {notice && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
          {notice}
        </div>
      )}

      <DataTable
        columns={columns}
        data={userList ?? []}
        loading={isLoading}
        emptyMessage="Noch keine Nutzer."
        rowKey={(u) => u.id}
      />

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            onDone('Nutzer angelegt — eine Einladung wurde versendet.')();
          }}
          onError={onError('Anlegen fehlgeschlagen.')}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          isSelf={editing.id === currentUser?.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onDone('Änderungen gespeichert.')();
          }}
          onError={onError('Speichern fehlgeschlagen.')}
        />
      )}

      {activityFor && (
        <ActivityModal user={activityFor} onClose={() => setActivityFor(null)} />
      )}

      <RegistryCredentialsSection isAdmin={isAdmin} />
    </div>
  );
}

/**
 * Who can pull from our container registry.
 *
 * Lives on this page because that is where someone goes when a colleague leaves —
 * and taking the account away without taking the registry access away is the kind
 * of half-offboarding nobody notices for months. Servers appear here too: the
 * question "who can pull our images" has both kinds of answer.
 */
function RegistryCredentialsSection({ isAdmin }: { isAdmin: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { dateTime } = useDateFormat();
  const [error, setError] = useState('');

  const { data = [], isLoading } = useQuery({
    queryKey: ['registry-credentials'],
    queryFn: registryCredentials.list,
    retry: false,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => registryCredentials.revoke(id),
    onSuccess: () => {
      setError('');
      void qc.invalidateQueries({ queryKey: ['registry-credentials'] });
      void qc.invalidateQueries({ queryKey: ['registry-credential-me'] });
    },
    onError: (err: unknown) =>
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Entziehen fehlgeschlagen.',
      ),
  });

  return (
    <div className="mt-8">
      <h2 className="text-sm font-semibold text-slate-800">{t('users.registryAccounts')}</h2>
      <p className="mt-0.5 text-xs text-slate-500">
        Wer Images aus unserer Registry ziehen kann. Jeder legt seinen eigenen unter
        „Mein Registry-Zugang" an; hier lassen sie sich einzeln entziehen — ohne die
        anderen mitzunehmen.
      </p>

      {error && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200 bg-surface">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              {['Zugang', 'Gehört zu', 'Angelegt', ''].map((h) => (
                <th
                  key={h}
                  className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-400">
                  Lade…
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-sm italic text-slate-400">
                  Noch keine eigenen Zugänge — es läuft alles über den gemeinsamen.
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-800">{row.username}</td>
                  <td className="px-4 py-2.5 text-slate-700">
                    {row.kind === 'user' ? (
                      <>
                        {row.user?.name ?? '—'}
                        <span className="ml-1.5 text-xs text-slate-400">{row.user?.email}</span>
                        {row.user?.status && row.user.status !== 'ACTIVE' && (
                          <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                            Konto {row.user.status === 'SUSPENDED' ? 'gesperrt' : 'inaktiv'}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="font-mono text-xs">{row.server?.hostname ?? '—'}</span>
                        {row.server?.ownership === 'CUSTOMER_PROVIDED' && (
                          <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                            Kundenserver
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">
                    {dateTime(row.createdAt)}
                    {row.rotatedAt && (
                      <div className="text-[11px] text-slate-400">
                        erneuert {dateTime(row.rotatedAt)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {isAdmin && (
                      <button
                        onClick={() => {
                          if (
                            confirm(
                              `Zugang "${row.username}" entziehen?\n\nAb sofort können damit keine Images mehr gezogen werden. Andere Zugänge sind nicht betroffen.`,
                            )
                          )
                            revoke.mutate(row.id);
                        }}
                        disabled={revoke.isPending}
                        className="text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        Entziehen
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Create ──────────────────────────────────────────────────────────────────

function CreateUserModal({
  onClose,
  onCreated,
  onError,
}: {
  onClose: () => void;
  onCreated: () => void;
  onError: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', role: 'SUPPORT' as UserRole });

  const mutation = useMutation({
    mutationFn: () => usersApi.create(form),
    onSuccess: onCreated,
    onError,
  });

  return (
    <Modal title={t('users.newUser')} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="px-6 py-5 space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
          <input
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <RoleSelect value={form.role} onChange={(role) => setForm((f) => ({ ...f, role }))} />

        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          Es wird kein Passwort vergeben. Der Nutzer erhält eine E-Mail mit einem Link, über den er
          sich selbst ein Passwort setzt (24 Stunden gültig).
        </p>

        <ModalActions
          onClose={onClose}
          pending={mutation.isPending}
          submitLabel="Anlegen"
          pendingLabel="Wird angelegt…"
        />
      </form>
    </Modal>
  );
}

// ─── Edit ────────────────────────────────────────────────────────────────────

function EditUserModal({
  user,
  isSelf,
  onClose,
  onSaved,
  onError,
}: {
  user: User;
  isSelf: boolean;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}) {
  const [form, setForm] = useState({
    name: user.name,
    role: user.role,
    status: user.status,
    slackId: user.slackId ?? '',
  });
  const [holMeldung, setHolMeldung] = useState('');
  const [vorschlaege, setVorschlaege] = useState<
    Array<{ id: string; name: string; email: string | null }>
  >([]);

  const mutation = useMutation({
    // Leerer Text bedeutet "keine Kennung", nicht "leerer Text" — sonst
    // stuende in der Datenbank ein leeres Feld, das wie eine gesetzte, aber
    // kaputte Kennung aussieht.
    mutationFn: () => usersApi.update(user.id, { ...form, slackId: form.slackId.trim() || null }),
    onSuccess: onSaved,
    onError,
  });

  return (
    <Modal title={`${user.name} bearbeiten`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="px-6 py-5 space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
          <input
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail</label>
          <input
            disabled
            value={user.email}
            className="w-full border border-slate-200 bg-slate-50 text-slate-500 rounded-lg px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-slate-500">
            An diese Adresse gehen auch Bereitschaftsmeldungen.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Slack-Mitglieds-ID
          </label>
          <div className="flex gap-2">
            <input
              value={form.slackId}
              onChange={(e) => setForm((f) => ({ ...f, slackId: e.target.value }))}
              placeholder="U01ABC23DEF"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              type="button"
              onClick={async () => {
                setHolMeldung('Wird bei Slack nachgefragt …');
                try {
                  const e = await usersApi.slackIdHolen(user.id);
                  setVorschlaege([]);
                  if (e.gefunden && e.slackId) {
                    setForm((f) => ({ ...f, slackId: e.slackId! }));
                    setHolMeldung('Gefunden und eingetragen — noch speichern.');
                  } else if (e.vorschlaege?.length) {
                    setVorschlaege(e.vorschlaege);
                    setHolMeldung(
                      `Slack kennt ${e.gepruefteAdresse ?? user.email} nicht — dort ist wohl ` +
                        'eine andere Adresse hinterlegt. Über den Namen gefunden:',
                    );
                  } else {
                    setHolMeldung(
                      `Slack kennt weder die Adresse ${e.gepruefteAdresse ?? user.email} noch ` +
                        'den Namen. ID von Hand eintragen: in Slack auf den Namen klicken → ' +
                        'Profil → ⋯ → „Mitglieds-ID kopieren".',
                    );
                  }
                } catch (err: any) {
                  setHolMeldung(err?.response?.data?.message ?? 'Nachfrage fehlgeschlagen.');
                }
              }}
              className="shrink-0 border border-slate-300 hover:border-slate-400 text-slate-700 text-sm px-3 rounded-lg"
              title="Fragt Slack nach der Mitglieds-ID zur Mailadresse dieses Kontos."
            >
              Abfragen
            </button>
          </div>
          {holMeldung && <p className="mt-1 text-xs text-slate-600">{holMeldung}</p>}
          {vorschlaege.length > 0 && (
            <ul className="mt-1 space-y-1">
              {vorschlaege.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setForm((f) => ({ ...f, slackId: v.id }));
                      setVorschlaege([]);
                      setHolMeldung(`${v.name} übernommen — noch speichern.`);
                    }}
                    className="w-full rounded border border-slate-200 px-2 py-1 text-left text-xs hover:border-teal-400 hover:bg-teal-50"
                  >
                    <span className="font-medium">{v.name}</span>{' '}
                    <span className="font-mono text-slate-500">{v.id}</span>
                    {v.email && <span className="text-slate-400"> · {v.email}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-xs text-slate-500">
            Für die direkte Ansprache bei Alarmen. In Slack: auf den Namen klicken →
            Profil → ⋯ → „Mitglieds-ID kopieren". Die ID, nicht der Handle — ein
            <span className="font-mono"> @name</span> im Text sieht richtig aus,
            löst aber keine Benachrichtigung aus.
          </p>
        </div>

        <RoleSelect
          value={form.role}
          onChange={(role) => setForm((f) => ({ ...f, role }))}
          disabled={isSelf}
          hint={isSelf ? 'Sie können Ihre eigene Rolle nicht ändern.' : undefined}
        />

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
          <select
            value={form.status}
            disabled={isSelf}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as UserStatus }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500"
          >
            <option value="ACTIVE">Aktiv</option>
            <option value="INACTIVE">Inaktiv</option>
            <option value="SUSPENDED">Gesperrt</option>
          </select>
          {isSelf && (
            <p className="text-xs text-slate-400 mt-1">
              Sie können Ihr eigenes Konto nicht deaktivieren.
            </p>
          )}
        </div>

        {/* Der Zugang zum Quelltext gehoert zu derselben Person und damit in
            denselben Vorgang. Ein zweiter Ort dafuer waere ein zweiter Ort zum
            Vergessen — und vergessen wird nicht das Anlegen, sondern das
            Entziehen. */}
        <GitKontoBereich userId={user.id} email={user.email} pcStatus={form.status} />

        {/* SSH-Zugang zu den Servern. Verteilt wird ueber authorized_keys --
            die Anbieter-APIs setzen Schluessel nur beim Erstellen einer
            Maschine, nachtraeglich geht dort nichts. */}
        {/* istAdmin immer wahr: diese Seite ist laut routeAccess nur fuer
            ADMIN erreichbar, und die Routen pruefen es serverseitig noch
            einmal. Wer hier steht, darf auch Schluessel sperren. */}
        <SshSchluesselPanel userId={user.id} istAdmin />

        <ModalActions
          onClose={onClose}
          pending={mutation.isPending}
          submitLabel="Speichern"
          pendingLabel="Wird gespeichert…"
        />
      </form>
    </Modal>
  );
}

// ─── Activity ────────────────────────────────────────────────────────────────

function ActivityModal({ user, onClose }: { user: User; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: logs, isLoading } = useQuery({
    queryKey: ['users', user.id, 'activity'],
    queryFn: () => usersApi.activity(user.id),
  });

  return (
    <Modal title={`Aktivität von ${user.name}`} onClose={onClose} wide>
      <div className="px-6 py-5">
        {isLoading ? (
          <div className="space-y-2 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-slate-100 rounded" />
            ))}
          </div>
        ) : !logs?.length ? (
          <p className="text-sm text-slate-400 italic">{t('users.noActivity')}</p>
        ) : (
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                  <th className="px-3 py-2 font-medium">Zeitpunkt</th>
                  <th className="px-3 py-2 font-medium">Aktion</th>
                  <th className="px-3 py-2 font-medium">Objekt</th>
                  <th className="px-3 py-2 font-medium">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log: AuditLog) => (
                  <tr key={log.id}>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap text-xs">
                      {new Date(log.createdAt).toLocaleString('de-DE')}
                    </td>
                    <td className="px-3 py-2">
                      <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                        {log.action}
                      </code>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">
                      {log.entityType ?? '—'}
                      {log.entityId && (
                        <span className="text-slate-400"> · {log.entityId.slice(0, 10)}…</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-400 text-xs">{log.ipAddress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────────────

function RoleSelect({
  value,
  onChange,
  disabled,
  hint,
}: {
  value: UserRole;
  onChange: (role: UserRole) => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">Rolle</label>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as UserRole)}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500"
      >
        {ALL_ROLES.map((role) => (
          <option key={role} value={role}>
            {ROLE_LABELS[role]}
          </option>
        ))}
      </select>
      <p className="text-xs text-slate-500 mt-1">{hint ?? ROLE_DESCRIPTIONS[value]}</p>
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-surface rounded-xl shadow-xl w-full max-h-screen overflow-y-auto ${wide ? 'max-w-3xl' : 'max-w-md'}`}
      >
        <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-surface">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({
  onClose,
  pending,
  submitLabel,
  pendingLabel,
}: {
  onClose: () => void;
  pending: boolean;
  submitLabel: string;
  pendingLabel: string;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600"
      >
        Abbrechen
      </button>
      <button
        type="submit"
        disabled={pending}
        className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg disabled:opacity-60"
      >
        {pending ? pendingLabel : submitLabel}
      </button>
    </div>
  );
}
